import { Text } from '@blocksuite/affine/store';

import type { DocsService } from '../doc';

const newId = () => crypto.randomUUID();
const DAY = 24 * 60 * 60 * 1000;

/**
 * TEMP DEV TOOL: builds a database with many rows and a very wide date span,
 * to measure how the gantt view behaves with big lists.
 */
export async function createStressDoc(
  docsService: DocsService,
  rows = 300,
  spanYears = 20
): Promise<string> {
  const docRecord = docsService.createDoc({ title: `Стресс: ${rows} задач` });
  const { doc, release } = docsService.open(docRecord.id);
  try {
    await doc.waitForSyncReady();
    const store = doc.blockSuiteDoc;
    const note = store.getBlocksByFlavour('affine:note').at(0);
    if (!note) return docRecord.id;

    const titleId = newId();
    const dateId = newId();
    const ownerId = newId();
    const base = new Date(2015, 0, 1).getTime();

    store.transact(() => {
      store.addBlock(
        'affine:paragraph',
        {
          type: 'h2',
          text: new Text(`Стресс-тест: ${rows} строк / ${spanYears} лет`),
        },
        note.id
      );

      const dbId = store.addBlock(
        'affine:database',
        {
          title: new Text('Стресс'),
          columns: [
            { id: titleId, type: 'title', name: 'Задача', data: {} },
            { id: dateId, type: 'date', name: 'Срок', data: {} },
            { id: ownerId, type: 'text', name: 'Ответственный', data: {} },
          ],
          views: [
            {
              id: newId(),
              name: 'Инфокарта',
              mode: 'gantt',
              columns: [
                { id: titleId, width: 200 },
                { id: dateId, width: 200 },
                { id: ownerId, width: 160 },
              ],
              filter: { type: 'group', op: 'and', conditions: [] },
              date: { startColumnId: dateId },
              header: { titleColumn: titleId },
              scale: 'month',
            },
          ],
          cells: {},
        },
        note.id
      );

      // Rows first: `addBlock` returns the real row id, and only then can the
      // cells map point at it. A made-up id silently yields a row without data.
      const rowIds: string[] = [];
      for (let i = 0; i < rows; i++) {
        rowIds.push(
          store.addBlock(
            'affine:paragraph',
            { text: new Text(`Задача ${i + 1}`) },
            dbId
          )
        );
      }

      const cells: Record<
        string,
        Record<string, { columnId: string; value: unknown }>
      > = {};
      rowIds.forEach((rowId, i) => {
        const start = base + ((i * 7919) % (spanYears * 365)) * DAY;
        const rowCells: Record<string, { columnId: string; value: unknown }> =
          {
            [titleId]: { columnId: titleId, value: `Задача ${i + 1}` },
            [ownerId]: {
              columnId: ownerId,
              value: `Исполнитель ${(i % 17) + 1}`,
            },
          };
        if (i % 10 !== 0) {
          rowCells[dateId] = { columnId: dateId, value: start };
        }
        cells[rowId] = rowCells;
      });

      store.updateBlock(dbId, { cells });
    });
  } finally {
    release();
  }

  return docRecord.id;
}
